import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase';

/**
 * Analytics Service for tracking user events and behavior
 * All methods are safe to call even if analytics is not initialized
 */

// Custom event names for your Solo Leveling platform
export enum AnalyticsEvent {
  // User Events
  USER_SIGN_UP = 'user_sign_up',
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  
  // Quest Events
  QUEST_CREATED = 'quest_created',
  QUEST_COMPLETED = 'quest_completed',
  QUEST_FAILED = 'quest_failed',
  QUEST_EDITED = 'quest_edited',
  QUEST_DELETED = 'quest_deleted',
  
  // AI Events
  AI_QUEST_GENERATED = 'ai_quest_generated',
  AI_PROGRAM_GENERATED = 'ai_program_generated',
  AI_CHAT_MESSAGE = 'ai_chat_message',
  
  // Level & Stats Events
  LEVEL_UP = 'level_up',
  STAT_ALLOCATED = 'stat_allocated',
  ACHIEVEMENT_UNLOCKED = 'achievement_unlocked',
  
  // Passive Skills Events
  PASSIVE_SKILL_CREATED = 'passive_skill_created',
  PASSIVE_SKILL_MILESTONE = 'passive_skill_milestone',
  
  // Program Events
  PROGRAM_CREATED = 'program_created',
  PROGRAM_STARTED = 'program_started',
  PROGRAM_COMPLETED = 'program_completed',
  
  // Shop Events
  ITEM_PURCHASED = 'item_purchased',
  THEME_CHANGED = 'theme_changed',
  
  // Subscription & Payment Events (Google Analytics 4 recommended events)
  SUBSCRIPTION_MODAL_VIEWED = 'view_promotion', // GA4 recommended event
  CHECKOUT_INITIATED = 'begin_checkout', // GA4 recommended event
  SUBSCRIPTION_PURCHASED = 'purchase', // GA4 recommended event
  SUBSCRIPTION_ACTIVATED = 'subscription_activated', // Custom event
  
  // Landing Page & A/B Testing Events
  LANDING_VIEW = 'landing_view',
  CTA_CLICK = 'cta_click',
  DEMO_START = 'demo_start',
  SIGNUP_STARTED = 'signup_started',
  SIGNUP_COMPLETE = 'signup_complete',
  DEMO_ACTIVITY = 'demo_activity',
  PAGE_EXIT = 'page_exit',
  LANDING_BOUNCE = 'landing_bounce',
  SUBSCRIPTION_CANCELLED = 'subscription_cancelled', // Custom event
  TRIAL_STARTED = 'trial_started', // Custom event
  TRIAL_EXPIRED = 'trial_expired', // Custom event
  SUBSCRIPTION_UPGRADED = 'subscription_upgraded', // Custom event
  SUBSCRIPTION_DOWNGRADED = 'subscription_downgraded', // Custom event
  PAYMENT_FAILED = 'payment_failed', // Custom event
  
  // Engagement Events
  DAILY_REWARD_CLAIMED = 'daily_reward_claimed',
  
  // Demo Events
  DEMO_BUTTON_CLICKED = 'demo_button_clicked',
  DEMO_STARTED = 'demo_started',
  DEMO_SESSION_ENDED = 'demo_session_ended',
  DEMO_QUEST_COMPLETED = 'demo_quest_completed',
  DEMO_CONVERSION_SIGNUP = 'demo_conversion_signup',
  DEMO_CONVERSION_SIGNIN = 'demo_conversion_signin',
  DEMO_BANNER_SIGNUP = 'demo_banner_signup',
  DEMO_BANNER_SIGNIN = 'demo_banner_signin',
  DEMO_MODAL_START = 'demo_modal_start',
  DEMO_MODAL_SIGNUP = 'demo_modal_signup',
  DEMO_MODAL_STEP_ADVANCED = 'demo_modal_step_advanced',
  CALENDAR_VIEWED = 'calendar_viewed',
  STATS_VIEWED = 'stats_viewed',
  
  // System Events
  PAGE_VIEW = 'page_view',
  ERROR_OCCURRED = 'error_occurred',
}

/**
 * Log a custom event with optional parameters
 */
export const trackEvent = (
  eventName: AnalyticsEvent | string,
  parameters?: Record<string, any>
): void => {
  if (!analytics) {
    console.debug('[Analytics] Not initialized, skipping event:', eventName);
    return;
  }

  try {
    logEvent(analytics, eventName, parameters);
    console.debug('[Analytics] Event tracked:', eventName, parameters);
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
  }
};

/**
 * Track page views
 */
export const trackPageView = (pageName: string, pageTitle?: string): void => {
  trackEvent(AnalyticsEvent.PAGE_VIEW, {
    page_name: pageName,
    page_title: pageTitle || pageName,
  });
};

/**
 * Set the user ID for tracking
 */
export const setAnalyticsUserId = (userId: string | null): void => {
  if (!analytics) return;

  try {
    if (userId) {
      setUserId(analytics, userId);
      console.debug('[Analytics] User ID set:', userId);
    } else {
      setUserId(analytics, null);
    }
  } catch (error) {
    console.error('[Analytics] Error setting user ID:', error);
  }
};

/**
 * Set user properties for segmentation
 */
export const setAnalyticsUserProperties = (properties: Record<string, any>): void => {
  if (!analytics) return;

  try {
    setUserProperties(analytics, properties);
    console.debug('[Analytics] User properties set:', properties);
  } catch (error) {
    console.error('[Analytics] Error setting user properties:', error);
  }
};

// Quest tracking helpers
export const trackQuestCreated = (questType: string, isAIGenerated: boolean): void => {
  trackEvent(AnalyticsEvent.QUEST_CREATED, {
    quest_type: questType,
    is_ai_generated: isAIGenerated,
  });
};

export const trackQuestCompleted = (
  questType: string,
  difficulty: string,
  timeToComplete?: number
): void => {
  trackEvent(AnalyticsEvent.QUEST_COMPLETED, {
    quest_type: questType,
    difficulty,
    time_to_complete: timeToComplete,
  });
};

export const trackQuestFailed = (questType: string, reason: string): void => {
  trackEvent(AnalyticsEvent.QUEST_FAILED, {
    quest_type: questType,
    failure_reason: reason,
  });
};

// Level & Stats tracking helpers
export const trackLevelUp = (newLevel: number, totalXP: number): void => {
  trackEvent(AnalyticsEvent.LEVEL_UP, {
    level: newLevel,
    total_xp: totalXP,
  });
};

export const trackStatAllocated = (statType: string, newValue: number): void => {
  trackEvent(AnalyticsEvent.STAT_ALLOCATED, {
    stat_type: statType,
    new_value: newValue,
  });
};

export const trackAchievementUnlocked = (achievementId: string, achievementName: string): void => {
  trackEvent(AnalyticsEvent.ACHIEVEMENT_UNLOCKED, {
    achievement_id: achievementId,
    achievement_name: achievementName,
  });
};

// AI tracking helpers
export const trackAIQuestGenerated = (promptLength: number, questsGenerated: number): void => {
  trackEvent(AnalyticsEvent.AI_QUEST_GENERATED, {
    prompt_length: promptLength,
    quests_generated: questsGenerated,
  });
};

export const trackAIProgramGenerated = (programType: string, durationWeeks: number): void => {
  trackEvent(AnalyticsEvent.AI_PROGRAM_GENERATED, {
    program_type: programType,
    duration_weeks: durationWeeks,
  });
};

export const trackAIChatMessage = (messageLength: number, responseTime?: number): void => {
  trackEvent(AnalyticsEvent.AI_CHAT_MESSAGE, {
    message_length: messageLength,
    response_time: responseTime,
  });
};

// Passive Skills tracking helpers
export const trackPassiveSkillCreated = (skillName: string, category: string): void => {
  trackEvent(AnalyticsEvent.PASSIVE_SKILL_CREATED, {
    skill_name: skillName,
    category,
  });
};

export const trackPassiveSkillMilestone = (
  skillName: string,
  milestoneNumber: number,
  daysToReach: number
): void => {
  trackEvent(AnalyticsEvent.PASSIVE_SKILL_MILESTONE, {
    skill_name: skillName,
    milestone: milestoneNumber,
    days_to_reach: daysToReach,
  });
};

// Program tracking helpers
export const trackProgramCreated = (programName: string, questCount: number): void => {
  trackEvent(AnalyticsEvent.PROGRAM_CREATED, {
    program_name: programName,
    quest_count: questCount,
  });
};

export const trackProgramStarted = (programName: string): void => {
  trackEvent(AnalyticsEvent.PROGRAM_STARTED, {
    program_name: programName,
  });
};

export const trackProgramCompleted = (programName: string, daysToComplete: number): void => {
  trackEvent(AnalyticsEvent.PROGRAM_COMPLETED, {
    program_name: programName,
    days_to_complete: daysToComplete,
  });
};

// Shop tracking helpers
export const trackItemPurchased = (itemName: string, itemPrice: number): void => {
  trackEvent(AnalyticsEvent.ITEM_PURCHASED, {
    item_name: itemName,
    price: itemPrice,
    currency: 'gold',
  });
};

export const trackThemeChanged = (themeName: string): void => {
  trackEvent(AnalyticsEvent.THEME_CHANGED, {
    theme_name: themeName,
  });
};

// Engagement tracking helpers
export const trackDailyRewardClaimed = (rewardType: string, streakDays: number): void => {
  trackEvent(AnalyticsEvent.DAILY_REWARD_CLAIMED, {
    reward_type: rewardType,
    streak_days: streakDays,
  });
};

// Error tracking
export const trackError = (errorName: string, errorMessage: string, errorStack?: string): void => {
  trackEvent(AnalyticsEvent.ERROR_OCCURRED, {
    error_name: errorName,
    error_message: errorMessage,
    error_stack: errorStack,
  });
};

// Subscription & Payment tracking helpers
/**
 * Track when user views the subscription modal
 * Uses GA4 recommended "view_promotion" event
 */
export const trackSubscriptionModalViewed = (currentTier: string, source?: string): void => {
  trackEvent(AnalyticsEvent.SUBSCRIPTION_MODAL_VIEWED, {
    promotion_id: 'subscription_plans',
    promotion_name: 'Solo Leveling Subscription',
    creative_name: 'subscription_modal',
    creative_slot: 'modal',
    current_tier: currentTier,
    source: source || 'unknown', // Where modal was opened from
  });
};

/**
 * Track when user initiates checkout (clicks on a plan)
 * Uses GA4 recommended "begin_checkout" event
 */
export const trackCheckoutInitiated = (
  tier: string,
  price: number,
  currency: string = 'USD'
): void => {
  trackEvent(AnalyticsEvent.CHECKOUT_INITIATED, {
    currency,
    value: price,
    items: [
      {
        item_id: `sub_${tier}`,
        item_name: `${tier.toUpperCase()}-Rank Subscription`,
        item_category: 'subscription',
        item_variant: tier,
        price,
        quantity: 1,
      },
    ],
    subscription_tier: tier,
  });
};

/**
 * Track successful subscription purchase
 * Uses GA4 recommended "purchase" event
 */
export const trackSubscriptionPurchased = (
  tier: string,
  price: number,
  transactionId: string,
  currency: string = 'USD',
  paymentMethod?: string
): void => {
  trackEvent(AnalyticsEvent.SUBSCRIPTION_PURCHASED, {
    currency,
    transaction_id: transactionId,
    value: price,
    items: [
      {
        item_id: `sub_${tier}`,
        item_name: `${tier.toUpperCase()}-Rank Subscription`,
        item_category: 'subscription',
        item_variant: tier,
        price,
        quantity: 1,
      },
    ],
    subscription_tier: tier,
    payment_method: paymentMethod || 'stripe',
  });
};

/**
 * Track when subscription becomes active (after purchase or webhook confirmation)
 */
export const trackSubscriptionActivated = (
  tier: string,
  previousTier: string,
  isUpgrade: boolean
): void => {
  trackEvent(AnalyticsEvent.SUBSCRIPTION_ACTIVATED, {
    subscription_tier: tier,
    previous_tier: previousTier,
    is_upgrade: isUpgrade,
    is_new_subscription: previousTier === 'trial' || previousTier === 'expired',
  });
};

/**
 * Track when user cancels their subscription
 */
export const trackSubscriptionCancelled = (
  tier: string,
  daysActive: number,
  cancelReason?: string
): void => {
  trackEvent(AnalyticsEvent.SUBSCRIPTION_CANCELLED, {
    subscription_tier: tier,
    days_active: daysActive,
    cancel_reason: cancelReason || 'not_specified',
  });
};

/**
 * Track when a new user starts their trial
 */
export const trackTrialStarted = (source?: string): void => {
  trackEvent(AnalyticsEvent.TRIAL_STARTED, {
    trial_duration_days: 14,
    source: source || 'signup',
  });
};

/**
 * Track when trial expires
 */
export const trackTrialExpired = (daysUsed: number, convertedToPaid: boolean): void => {
  trackEvent(AnalyticsEvent.TRIAL_EXPIRED, {
    days_used: daysUsed,
    converted_to_paid: convertedToPaid,
  });
};

/**
 * Track subscription upgrades (e.g., E-Rank → S-Rank)
 */
export const trackSubscriptionUpgraded = (
  fromTier: string,
  toTier: string,
  priceIncrease: number
): void => {
  trackEvent(AnalyticsEvent.SUBSCRIPTION_UPGRADED, {
    from_tier: fromTier,
    to_tier: toTier,
    price_increase: priceIncrease,
  });
};

/**
 * Track subscription downgrades (e.g., S-Rank → E-Rank)
 */
export const trackSubscriptionDowngraded = (
  fromTier: string,
  toTier: string,
  priceDecrease: number
): void => {
  trackEvent(AnalyticsEvent.SUBSCRIPTION_DOWNGRADED, {
    from_tier: fromTier,
    to_tier: toTier,
    price_decrease: priceDecrease,
  });
};

/**
 * Track payment failures
 */
export const trackPaymentFailed = (
  tier: string,
  errorCode: string,
  errorMessage: string
): void => {
  trackEvent(AnalyticsEvent.PAYMENT_FAILED, {
    subscription_tier: tier,
    error_code: errorCode,
    error_message: errorMessage,
  });
};

